import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, errMsg, fmtMoney } from "../../lib/api";
import { SERVICES } from "../../lib/constants";
import { fileToDataUrl } from "../../lib/upload";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { AddressInput } from "../../components/shared/AddressInput";
import { toast } from "sonner";
import {
  Loader2, Copy, MessageCircle, Smartphone, ExternalLink, Send, Plus, Minus,
  Package, CheckCircle2, User, Link2, DollarSign, Boxes, X, Camera, ImageIcon,
} from "lucide-react";

const SIZES = [["single", "Single item"], ["few", "Few items"], ["studio", "Studio / 1BR"], ["2br", "2 Bedroom"], ["3br", "3 Bedroom"], ["whole_house", "Whole house"]];

export default function CreateJob() {
  const nav = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [form, setForm] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    title: "", status: "pending",
    service_type: "moving", size: "few",
    pickup_address: "", pickup_lat: null, pickup_lng: null,
    dropoff_address: "", dropoff_lat: null, dropoff_lng: null,
    same_place: false,
    scheduled_date: "", scheduled_time: "09:00",
    stairs: false, stairs_flights: 1, electrical: false,
    extra_stops: 0, priority: false, notes: "",
    manual_total: "", crew_payout: "",
  });
  const [items, setItems] = useState([]);   // [{name, qty, spec}]
  const [lineItems, setLineItems] = useState([]);  // [{label, qty, unit_price}]
  const [photos, setPhotos] = useState([]);  // item photos (data URLs) shown on crew live-job screen
  const [estimate, setEstimate] = useState(null);
  const [route, setRoute] = useState(null);
  const [created, setCreated] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/bookings/catalog").then((r) => setCatalog(r.data)); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Live estimate as inputs change
  useEffect(() => {
    if (!form.service_type) return;
    const payload = {
      service_type: form.service_type, size: form.size, same_place: form.same_place,
      distance_miles: route?.distance_miles || 0, stairs: form.stairs,
      stairs_flights: form.stairs_flights, electrical: form.electrical,
      extra_stops: form.extra_stops, priority: form.priority, items,
    };
    api.post("/bookings/estimate", payload).then((r) => setEstimate(r.data)).catch(() => {});
  }, [form.service_type, form.size, form.same_place, form.stairs, form.stairs_flights,
      form.electrical, form.extra_stops, form.priority, route?.distance_miles, items]);

  // Get driving distance whenever both endpoints have coords
  useEffect(() => {
    if (form.same_place || !form.pickup_lat || !form.dropoff_lat) { setRoute(null); return; }
    api.post("/geo/route", {
      pickup: { label: form.pickup_address, lat: form.pickup_lat, lng: form.pickup_lng },
      dropoff: { label: form.dropoff_address, lat: form.dropoff_lat, lng: form.dropoff_lng },
    }).then((r) => setRoute(r.data)).catch(() => setRoute(null));
  }, [form.pickup_lat, form.dropoff_lat, form.same_place]); // eslint-disable-line

  const upsertItem = (name, patch) => setItems((prev) => {
    const i = prev.findIndex((x) => x.name === name);
    if (i < 0) return [...prev, { name, qty: 1, spec: "", ...patch }];
    const n = [...prev]; n[i] = { ...n[i], ...patch }; return n;
  });
  const incItem = (name, d) => setItems((prev) => {
    const i = prev.findIndex((x) => x.name === name);
    if (i < 0 && d > 0) return [...prev, { name, qty: 1, spec: "" }];
    if (i < 0) return prev;
    const q = prev[i].qty + d;
    if (q <= 0) return prev.filter((_, j) => j !== i);
    const n = [...prev]; n[i] = { ...n[i], qty: q }; return n;
  });

  const lineItemsTotal = useMemo(
    () => lineItems.reduce((s, li) => s + (Number(li.qty) || 0) * (Number(li.unit_price) || 0), 0),
    [lineItems]);

  const total = useMemo(() => {
    if (form.manual_total && Number(form.manual_total) > 0) return Math.round(Number(form.manual_total));
    if (lineItemsTotal > 0) return Math.round(lineItemsTotal);
    return estimate?.subtotal || 0;
  }, [form.manual_total, lineItemsTotal, estimate]);

  const addLine = (label = "") => setLineItems((p) => [...p, { label, qty: 1, unit_price: "" }]);
  const updateLine = (i, patch) => setLineItems((p) => p.map((li, j) => (j === i ? { ...li, ...patch } : li)));
  const removeLine = (i) => setLineItems((p) => p.filter((_, j) => j !== i));

  const addPhotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6 - photos.length);
    if (!files.length) return;
    const converted = await Promise.all(files.map((f) => fileToDataUrl(f, 1280).catch(() => null)));
    const ok = converted.filter(Boolean);
    setPhotos((p) => [...p, ...ok].slice(0, 6));
    e.target.value = "";
  };

  const submit = async () => {
    if (!form.customer_name.trim() || !form.pickup_address.trim()) {
      toast.error("Customer name and pickup address are required"); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/ops/jobs/create-for-customer", {
        customer_name: form.customer_name.trim(),
        customer_email: form.customer_email.trim() || null,
        customer_phone: form.customer_phone.trim(),
        title: form.title.trim(), status: form.status,
        service_type: form.service_type, size: form.size,
        pickup_address: form.pickup_address, dropoff_address: form.dropoff_address,
        same_place: form.same_place, distance_miles: route?.distance_miles || 0,
        scheduled_date: form.scheduled_date, scheduled_time: form.scheduled_time,
        items, stairs: form.stairs, stairs_flights: form.stairs_flights,
        electrical: form.electrical, extra_stops: form.extra_stops,
        priority: form.priority, notes: form.notes,
        line_items: lineItems.filter((li) => li.label.trim()).map((li) => ({
          label: li.label.trim(), qty: Number(li.qty) || 1, unit_price: Number(li.unit_price) || 0,
        })),
        manual_total: form.manual_total ? Number(form.manual_total) : null,
        crew_payout: form.crew_payout ? Number(form.crew_payout) : 0,
        photos,
      });
      setCreated(data);
      toast.success(`${data.mission.mission_number} created — share the link now`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  if (created) {
    const s = created.share;
    const fullLink = window.location.origin + s.link;
    return (
      <div className="max-w-2xl mx-auto fade-up" data-testid="createjob-success">
        <div className="text-center mb-8">
          <CheckCircle2 className="w-14 h-14 text-[var(--cc-accent)] mx-auto" />
          <p className="overline-cc mt-3">Job Created</p>
          <h1 className="page-title mt-1.5">{created.mission.mission_number}</h1>
          <p className="text-muted-foreground mt-2">Share the link — customer opens it, creates an account, and pays online.</p>
        </div>

        <div className="rounded-2xl border-2 border-[var(--cc-accent)]/40 bg-[var(--cc-accent)]/5 p-6 space-y-4">
          <div>
            <p className="text-xs font-mono-cc uppercase text-muted-foreground mb-1.5">Magic link</p>
            <div className="flex gap-2">
              <Input value={fullLink} readOnly className="h-11 text-sm font-mono-cc" data-testid="createjob-link-input" />
              <Button variant="outline" size="icon" className="rounded-xl shrink-0 h-11 w-11" data-testid="createjob-copy-link"
                onClick={() => { navigator.clipboard.writeText(fullLink); toast.success("Link copied"); }}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div>
            <p className="text-xs font-mono-cc uppercase text-muted-foreground mb-1.5">Prewritten SMS / WhatsApp message</p>
            <Textarea value={s.message} readOnly rows={3} className="text-sm font-mono-cc" data-testid="createjob-msg" />
            <Button variant="outline" size="sm" className="rounded-full mt-2" data-testid="createjob-copy-msg"
              onClick={() => { navigator.clipboard.writeText(s.message); toast.success("Message copied"); }}>
              <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy message
            </Button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 pt-2">
            {s.sms_url && (
              <a href={s.sms_url} target="_blank" rel="noreferrer" data-testid="createjob-sms-link">
                <Button className="w-full h-12 rounded-xl bg-[var(--cc-accent)] text-black hover:bg-[var(--cc-accent)]/90 font-bold btn-glow">
                  <MessageCircle className="w-4 h-4 mr-2" /> Send via SMS <ExternalLink className="w-3.5 h-3.5 ml-auto opacity-70" />
                </Button>
              </a>
            )}
            <a href={s.whatsapp_url} target="_blank" rel="noreferrer" data-testid="createjob-whatsapp-link">
              <Button variant="outline" className="w-full h-12 rounded-xl font-semibold">
                <Smartphone className="w-4 h-4 mr-2 text-[#25D366]" /> Send via WhatsApp <ExternalLink className="w-3.5 h-3.5 ml-auto opacity-70" />
              </Button>
            </a>
          </div>
        </div>

        <div className="flex justify-center gap-3 mt-6">
          <Button variant="outline" className="rounded-full" onClick={() => { setCreated(null); setItems([]); setLineItems([]); setPhotos([]); }}>
            <Plus className="w-4 h-4 mr-1.5" /> Create another
          </Button>
          <Link to="/ops/missions"><Button className="rounded-full">View all missions</Button></Link>
        </div>
      </div>
    );
  }

  const itemList = ["Couch / Sofa", "Bed frame", "Mattress", "Dresser", "Dining set", "Appliance", "TV",
                    "Boxes", "Exercise equipment", "Special delivery"];

  return (
    <div className="space-y-6" data-testid="createjob-page">
      <div>
        <p className="overline-cc">Create Job for Customer</p>
        <h1 className="page-title mt-1.5 flex items-center gap-3"><Link2 className="w-8 h-8 text-[var(--cc-accent)]" /> Job Link Builder</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">Build a full job for a customer, send them the magic link, and let them accept, create an account, track it live, and pay — all in one flow.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* LEFT: Inputs */}
        <div className="space-y-5">
          <div className="rounded-xl border bg-card p-5 space-y-4">
            <p className="stat-label flex items-center gap-2"><User className="w-3.5 h-3.5" /> Customer</p>
            <div className="space-y-1"><Label className="text-xs font-bold">Name *</Label>
              <Input data-testid="createjob-name" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} placeholder="Jordan Smith" />
              <p className="text-[11px] text-muted-foreground">The only required field. Everything else the customer fills in when they open the link.</p></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs font-bold flex items-center gap-1.5">Email <span className="text-[10px] font-mono-cc uppercase opacity-60">optional</span></Label>
                <Input data-testid="createjob-email" type="email" value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} placeholder="them@email.com (or leave blank)" /></div>
              <div className="space-y-1"><Label className="text-xs font-bold flex items-center gap-1.5">Phone <span className="text-[10px] font-mono-cc uppercase opacity-60">optional</span></Label>
                <Input data-testid="createjob-phone" value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} placeholder="(619) 555-0100" />
                <p className="text-[11px] text-muted-foreground">Fill in only if you want the pre-written SMS/WhatsApp share button.</p></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 pt-1">
              <div className="space-y-1"><Label className="text-xs font-bold">Job title</Label>
                <Input data-testid="createjob-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Living room move — Jordan" />
                <p className="text-[11px] text-muted-foreground">Leave blank to auto-name from the service.</p></div>
              <div className="space-y-1"><Label className="text-xs font-bold">Initial status</Label>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger data-testid="createjob-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="pending_customer">Awaiting customer</SelectItem>
                    <SelectItem value="booked">Booked</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="assigned">Assigned</SelectItem>
                    <SelectItem value="in_progress">In progress</SelectItem>
                  </SelectContent>
                </Select></div>
            </div>
          </div>

          <div className="rounded-xl border bg-card p-5 space-y-4">
            <p className="stat-label">Service & Route</p>
            <div className="flex flex-wrap gap-2">
              {SERVICES.map((s) => (
                <button key={s.id} data-testid={`createjob-service-${s.id}`} onClick={() => set("service_type", s.id)}
                  className={`rounded-lg border px-3 py-2 text-xs font-semibold transition-colors ${form.service_type === s.id ? "border-[var(--cc-accent)] bg-[var(--cc-accent)]/10 text-[var(--cc-accent)]" : "hover:border-foreground/25"}`}>
                  {s.name}
                </button>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs font-bold">Job size</Label>
                <Select value={form.size} onValueChange={(v) => set("size", v)}>
                  <SelectTrigger data-testid="createjob-size"><SelectValue /></SelectTrigger>
                  <SelectContent>{SIZES.map(([id, l]) => <SelectItem key={id} value={id}>{l}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="flex items-end pb-1">
                <button data-testid="createjob-same-place" onClick={() => set("same_place", !form.same_place)}
                  className={`w-full rounded-lg border px-3 py-2.5 text-xs font-semibold ${form.same_place ? "border-[var(--cc-accent)] bg-[var(--cc-accent)]/10 text-[var(--cc-accent)]" : ""}`}>
                  Same-place move {form.same_place ? "✓" : ""}</button>
              </div>
            </div>
            <div className="space-y-1"><Label className="text-xs font-bold">Pickup *</Label>
              <AddressInput testid="createjob-pickup" value={form.pickup_address} placeholder="Start typing pickup address..."
                onChange={(t) => setForm((f) => ({ ...f, pickup_address: t, pickup_lat: null, pickup_lng: null }))}
                onSelect={(r) => setForm((f) => ({ ...f, pickup_address: r.label, pickup_lat: r.lat, pickup_lng: r.lng }))} />
            </div>
            {!form.same_place && (
              <div className="space-y-1"><Label className="text-xs font-bold">Dropoff</Label>
                <AddressInput testid="createjob-dropoff" value={form.dropoff_address} placeholder="310 Mission Ave, Oceanside"
                  onChange={(t) => setForm((f) => ({ ...f, dropoff_address: t, dropoff_lat: null, dropoff_lng: null }))}
                  onSelect={(r) => setForm((f) => ({ ...f, dropoff_address: r.label, dropoff_lat: r.lat, dropoff_lng: r.lng }))} />
                {route && <p className="text-xs font-mono-cc text-[var(--cc-accent)] mt-1">{route.distance_miles} MI · {route.duration_min} MIN</p>}
              </div>
            )}
          </div>

          <div className="rounded-xl border bg-card p-5 space-y-3">
            <p className="stat-label flex items-center gap-2"><Package className="w-3.5 h-3.5" /> Items</p>
            <div className="grid grid-cols-2 gap-2">
              {itemList.map((name) => {
                const cur = items.find((x) => x.name === name);
                const meta = catalog?.items?.find((i) => i.name === name);
                if (!cur) return (
                  <button key={name} data-testid={`createjob-item-${name.replace(/[^a-zA-Z0-9]+/g, "-")}`}
                    onClick={() => incItem(name, 1)}
                    className="rounded-lg border px-3 py-2 text-xs font-semibold text-left hover:border-foreground/25">
                    + {name}
                  </button>
                );
                return (
                  <div key={name} className="rounded-lg border border-[var(--cc-accent)]/40 bg-[var(--cc-accent)]/5 px-2 py-1.5 flex flex-col gap-1">
                    <div className="flex items-center justify-between gap-1">
                      <button onClick={() => incItem(name, -1)} className="w-6 h-6 rounded-full border flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                      <span className="text-xs font-semibold truncate">{name}{cur.qty > 1 ? ` ×${cur.qty}` : ""}</span>
                      <button onClick={() => incItem(name, 1)} className="w-6 h-6 rounded-full border flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                    </div>
                    {meta && (
                      <Select value={cur.spec || ""} onValueChange={(v) => upsertItem(name, { spec: v })}>
                        <SelectTrigger className="h-7 text-[11px]"><SelectValue placeholder="Details..." /></SelectTrigger>
                        <SelectContent>{meta.options.map((o) => <SelectItem key={o.id} value={o.id} className="text-xs">{o.label}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="rounded-xl border bg-card p-5 space-y-3">
            <p className="stat-label flex items-center gap-2"><Camera className="w-3.5 h-3.5" /> Item Photos <span className="font-mono-cc text-[10px] normal-case opacity-60">shown to crew on the live-job screen</span></p>
            <div className="flex flex-wrap gap-2.5">
              {photos.map((p, i) => (
                <div key={i} className="relative w-20 h-20 rounded-xl overflow-hidden border" data-testid={`createjob-photo-${i}`}>
                  <img src={p} alt={`Item ${i + 1}`} className="w-full h-full object-cover" />
                  <button type="button" data-testid={`createjob-photo-remove-${i}`} onClick={() => setPhotos((x) => x.filter((_, j) => j !== i))}
                    className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 text-white flex items-center justify-center"><X className="w-3 h-3" /></button>
                </div>
              ))}
              {photos.length < 6 && (
                <label data-testid="createjob-photo-add"
                  className="w-20 h-20 rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-1 cursor-pointer text-muted-foreground hover:border-[var(--cc-accent)] transition-colors">
                  <ImageIcon className="w-5 h-5" />
                  <span className="text-[10px] font-semibold">Add</span>
                  <input type="file" accept="image/*" multiple className="hidden" onChange={addPhotos} />
                </label>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground">Upload up to 6 photos of the items. The crew sees the first one in their job details.</p>
          </div>

          <div className="rounded-xl border bg-card p-5 space-y-3">
            <div className="flex items-center justify-between">
              <p className="stat-label flex items-center gap-2"><Boxes className="w-3.5 h-3.5" /> Line Items <span className="font-mono-cc text-[10px] normal-case opacity-60">priced individually · total = sum</span></p>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" className="rounded-full h-7 text-xs" data-testid="createjob-add-box" onClick={() => addLine("Box")}>
                  <Plus className="w-3 h-3 mr-1" /> Box
                </Button>
                <Button type="button" variant="outline" size="sm" className="rounded-full h-7 text-xs" data-testid="createjob-add-line" onClick={() => addLine("")}>
                  <Plus className="w-3 h-3 mr-1" /> Line
                </Button>
              </div>
            </div>
            {lineItems.length === 0 && (
              <p className="text-[11px] text-muted-foreground">Add priced lines (e.g. several boxes of different sizes). When you add any, the job total becomes the sum of these lines.</p>
            )}
            <div className="space-y-2">
              {lineItems.map((li, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`createjob-line-${i}`}>
                  <Input className="flex-1 h-9 text-sm" placeholder="Label (e.g. Large box)" value={li.label}
                    data-testid={`createjob-line-label-${i}`} onChange={(e) => updateLine(i, { label: e.target.value })} />
                  <Input className="w-14 h-9 text-sm text-center" type="number" min="1" placeholder="Qty" value={li.qty}
                    data-testid={`createjob-line-qty-${i}`} onChange={(e) => updateLine(i, { qty: e.target.value })} />
                  <div className="relative w-24">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
                    <Input className="h-9 text-sm pl-5" type="number" min="0" placeholder="0" value={li.unit_price}
                      data-testid={`createjob-line-price-${i}`} onChange={(e) => updateLine(i, { unit_price: e.target.value })} />
                  </div>
                  <span className="w-16 text-right text-xs font-mono-cc font-semibold">{fmtMoney((Number(li.qty) || 0) * (Number(li.unit_price) || 0))}</span>
                  <button type="button" onClick={() => removeLine(i)} data-testid={`createjob-line-remove-${i}`}
                    className="w-7 h-7 rounded-full border flex items-center justify-center text-muted-foreground hover:text-red-500 shrink-0"><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
            {lineItemsTotal > 0 && (
              <div className="border-t pt-2 flex justify-between text-sm font-heading font-bold" data-testid="createjob-lineitems-total">
                <span>Line items total</span><span>{fmtMoney(lineItemsTotal)}</span>
              </div>
            )}
          </div>

          <div className="rounded-xl border bg-card p-5 space-y-3">
            <p className="stat-label">Schedule & Extras</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs font-bold">Date</Label>
                <Input type="date" data-testid="createjob-date" value={form.scheduled_date} onChange={(e) => set("scheduled_date", e.target.value)} /></div>
              <div className="space-y-1"><Label className="text-xs font-bold">Time</Label>
                <Input type="time" data-testid="createjob-time" value={form.scheduled_time} onChange={(e) => set("scheduled_time", e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {[["stairs", "Stairs"], ["electrical", "Electrical"], ["priority", "Priority"]].map(([k, label]) => (
                <button key={k} data-testid={`createjob-toggle-${k}`} onClick={() => set(k, !form[k])}
                  className={`rounded-lg border px-3 py-2 text-xs font-semibold text-left ${form[k] ? "border-[var(--cc-accent)] bg-[var(--cc-accent)]/10 text-[var(--cc-accent)]" : ""}`}>
                  {label} {form[k] ? "✓" : ""}
                </button>
              ))}
            </div>
            <Textarea data-testid="createjob-notes" rows={2} placeholder="Internal notes / special instructions..." value={form.notes} onChange={(e) => set("notes", e.target.value)} />
          </div>
        </div>

        {/* RIGHT: Preview + Money */}
        <div className="space-y-5">
          <div className="rounded-xl border bg-card p-5 corner-cc">
            <p className="stat-label">Charge Customer</p>
            <p className="font-heading font-extrabold text-5xl mt-2 text-[var(--cc-accent)]" data-testid="createjob-total">{fmtMoney(total)}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {form.manual_total ? "Manual override" : lineItemsTotal > 0 ? "Sum of line items" : total > 0 ? "Rate card total" : "Add line items or a price to charge the job"}
            </p>
            <div className="mt-4 space-y-2">
              <Label className="text-xs font-bold">Manual price override (optional)</Label>
              <Input data-testid="createjob-manual-total" type="number" value={form.manual_total} onChange={(e) => set("manual_total", e.target.value)} placeholder="Leave blank to use line items / rate card" />
            </div>
          </div>

          {estimate && lineItemsTotal === 0 && !form.manual_total && (
            <div className="rounded-xl border bg-card p-5 space-y-2 text-sm" data-testid="createjob-breakdown">
              <p className="stat-label mb-2">Breakdown</p>
              <Line label={`Weight tier — ${estimate.weight_tier_label}`} amt={estimate.weight_tier_price} />
              {estimate.scope_baseline > estimate.weight_tier_price && <Line label="Scope baseline (higher)" amt={estimate.scope_baseline} />}
              {estimate.distance_fee > 0 && <Line label={`Distance ${estimate.billable_miles} mi`} amt={estimate.distance_fee} />}
              {estimate.stairs_fee > 0 && <Line label={`Stairs × ${estimate.stairs_flights}`} amt={estimate.stairs_fee} />}
              {estimate.electrical_fee > 0 && <Line label="Electrical" amt={estimate.electrical_fee} />}
              {estimate.priority_fee > 0 && <Line label="Priority" amt={estimate.priority_fee} />}
              {estimate.special_delivery_handling > 0 && <Line label="Special handling" amt={estimate.special_delivery_handling} />}
              <div className="border-t pt-2 flex justify-between font-heading font-bold"><span>Engine subtotal</span><span>{fmtMoney(estimate.subtotal)}</span></div>
            </div>
          )}

          <div className="rounded-xl border bg-card p-5 space-y-3">
            <p className="stat-label flex items-center gap-1.5"><DollarSign className="w-3.5 h-3.5" /> Crew Payout (visible to helpers)</p>
            <Input data-testid="createjob-crew-payout" type="number" value={form.crew_payout} onChange={(e) => set("crew_payout", e.target.value)} placeholder="What you'll pay the crew" />
            <p className="text-[11px] text-muted-foreground">Only the crew you assign will see this number — never the customer.</p>
          </div>

          <Button data-testid="createjob-submit" onClick={submit} disabled={busy || total <= 0 || !form.customer_name.trim()}
            className="w-full h-14 rounded-full bg-[var(--cc-accent)] text-[var(--cc-accent-fg)] hover:bg-[var(--cc-accent)]/90 font-bold text-base btn-glow">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-5 h-5 mr-2" /> Create Job & Get Link — {fmtMoney(total)}</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

const Line = ({ label, amt }) => (
  <div className="flex justify-between"><span className="text-muted-foreground">{label}</span><span className="font-mono-cc font-semibold">{fmtMoney(amt)}</span></div>
);
